import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { api, track } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const STEPS = [
  ["The property", "the_property"],
  ["Why we bought it", "why_we_bought"],
  ["Starting condition", "starting_condition"],
  ["The renovation", "the_renovation"],
  ["Problems encountered", "problems_encountered"],
  ["How we solved them", "how_we_solved"],
  ["Renovation scope", "renovation_scope"],
  ["Outcome", "outcome"],
  ["Lessons learned", "lessons_learned"],
];

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSeo({ title: p ? `${p.name}, ${p.location} | Emerald Crown Projects` : "Case Study | Emerald Crown", description: p?.summary || "Renovation case study by Emerald Crown Property Investments.", path: `/projects/${slug}` });

  useEffect(() => {
    track("page_view", { page: "project_detail", slug });
    api.get(`/projects/${slug}`).then(({ data }) => setP(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-24 text-center">
      <p className="text-slate-600">Project not found.</p>
      <Link to="/projects" className="text-emerald-800 underline mt-3 inline-block">Back to projects</Link>
    </div>
  );
  if (!p) return <div className="pt-40 pb-24 text-center text-slate-400">Loading…</div>;

  const gallery = [p.before_img, p.during_img, p.after_img, ...(p.gallery || [])].filter(Boolean);
  const stages = [["Before", p.before_img], ["During", p.during_img], ["After", p.after_img]].filter(([, v]) => v);

  return (
    <div className="pt-20">
      <section className="relative h-[52vh] min-h-[380px]">
        {p.hero_img && <img src={p.hero_img} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(2,20,15,0.94) 0%, rgba(2,20,15,0.45) 45%, rgba(2,20,15,0.12) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-12">
          <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> All projects</Link>
          <span className="font-mono uppercase tracking-[0.2em] text-xs font-semibold text-white" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.8)" }}>{p.type} · {p.location}</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-serif font-semibold text-white" data-testid="project-detail-title">{p.name}</h1>
        </div>
      </section>

      {stages.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {stages.map(([label, img]) => (
              <Reveal key={label}>
                <figure className="rounded-2xl overflow-hidden border border-slate-200/80 bg-white">
                  <div className="relative"><img src={img} alt={`${p.name} ${label}`} className="w-full h-56 object-cover" /><figcaption className="absolute top-3 left-3 text-[11px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-full bg-emerald-950/85 text-brass">{label}</figcaption></div>
                </figure>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {STEPS.filter(([, k]) => p[k]).map(([label, k], i) => (
          <Reveal key={k} delay={i * 40}>
            <div className="py-6 border-b border-slate-200/70">
              <span className="eyebrow">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="mt-2 text-2xl font-serif text-slate-900">{label}</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">{p[k]}</p>
            </div>
          </Reveal>
        ))}

        {p.show_financials && (p.purchase_price || p.renovation_cost || p.gdv) && (
          <div className="py-8">
            <span className="eyebrow">Financial overview</span>
            <div className="mt-4 grid grid-cols-3 gap-4">
              {[["Purchase", p.purchase_price], ["Renovation", p.renovation_cost], ["End value", p.gdv]].filter(([, v]) => v).map(([l, v]) => (
                <div key={l} className="rounded-xl bg-emerald-50 border border-emerald-100 p-5 text-center">
                  <div className="font-mono text-lg text-emerald-900">{v}</div>
                  <div className="text-xs text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <span className="eyebrow">Gallery</span>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.map((g, i) => (<img key={i} src={g} alt={`${p.name} gallery ${i + 1}`} className="rounded-xl w-full h-48 object-cover" />))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-emerald-900 px-8 py-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif text-white">Have a property like this?</h2>
          <p className="mt-3 text-emerald-50/80">We'd love to hear about it.</p>
          <Link to="/sell"><Button className="mt-6 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-8">Sell us your property <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
